import React from 'react';
import PropTypes from 'prop-types';
import { SearchIcon, XIcon } from '../../icons';
import Input from './Input';

/**
 * SearchInput Component
 * 
 * Specialized input component for search functionality.
 * Includes search icon and optional clear button.
 * 
 * @example
 * // Basic search input
 * <SearchInput
 *   value={searchQuery}
 *   onChange={(e) => setSearchQuery(e.target.value)}
 *   placeholder="Search websites..."
 * />
 * 
 * @example
 * // Search with clear callback
 * <SearchInput
 *   value={searchTerm}
 *   onChange={(e) => setSearchTerm(e.target.value)}
 *   onClear={() => {
 *     setSearchTerm('');
 *     performSearch('');
 *   }}
 * />
 */
const SearchInput = React.forwardRef(({
  // Value and change handlers
  value = '',
  onChange,
  onClear,
  
  // Input attributes
  placeholder = 'Search...',
  disabled = false,
  autoFocus = false,
  
  // Sizing
  size = 'md',
  fullWidth = true,
  
  // Styling
  className = '',
  
  // Show/hide icons
  showSearchIcon = true,
  showClearButton = true,
  
  // Additional props to pass to Input
  ...rest
}, ref) => {
  // Handle clear button click
  const handleClear = () => {
    if (onClear) {
      onClear();
    } else if (onChange) {
      // Simulate change event with empty value
      onChange({ target: { value: '', name: rest.name } });
    }
  };
  
  // Determine icons
  const leftIcon = showSearchIcon ? <SearchIcon size="sm" /> : undefined;
  
  const rightIcon = showClearButton && value ? (
    <button
      type="button"
      onClick={handleClear}
      className="text-gray-400 dark:text-dark-500 hover:text-gray-600 dark:hover:text-dark-300 transition-colors pointer-events-auto"
      aria-label="Clear search"
    >
      <XIcon size="sm" />
    </button>
  ) : undefined;
  
  return (
    <Input
      ref={ref}
      type="search"
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      disabled={disabled}
      autoFocus={autoFocus}
      size={size}
      fullWidth={fullWidth}
      leftIcon={leftIcon}
      rightIcon={rightIcon}
      className={className}
      autoComplete="off"
      {...rest}
    />
  );
});

SearchInput.displayName = 'SearchInput';

SearchInput.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  onClear: PropTypes.func,
  
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  autoFocus: PropTypes.bool,
  
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  fullWidth: PropTypes.bool,
  
  className: PropTypes.string,
  
  showSearchIcon: PropTypes.bool,
  showClearButton: PropTypes.bool,
};

export default SearchInput;
